import { getDecodedToken } from '@webclient/lib/jwt';

import appSettingsMiddleware from '@webclient/state/middleware/app-settings';
import searchSessionMiddleware from '@webclient/state/middleware/search-session';
import searchWidgetDictationMiddleware from '@webclient/state/middleware/search-widget-dictation';
import appSettingsSlice from '@webclient/state/slices/app-settings';
import authSlice from '@webclient/state/slices/auth';
import flowRunSlice, {
  onRunCommandListener,
  tickListener,
} from '@webclient/state/slices/flow-run';
import liveSlice from '@webclient/state/slices/live';
import liveSessionSlice, {
  onThreadIdListener,
} from '@webclient/state/slices/live-session';
import searchSessionSlice, {
  voiceChatModeListener,
} from '@webclient/state/slices/search-session';
import searchWidgetDictationSlice from '@webclient/state/slices/search-widget-dictation';

import { UserSpecificSettingsType } from '@webclient/shared-types/auth';

import { configureStore } from '@reduxjs/toolkit';
import { throttle } from 'lodash';

const store = configureStore({
  reducer: {
    appSettings: appSettingsSlice,
    live: liveSlice,
    flowRun: flowRunSlice,
    liveSession: liveSessionSlice,
    searchSession: searchSessionSlice,
    searchWidgetDictation: searchWidgetDictationSlice,
    auth: authSlice,
  },
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware().concat([
      appSettingsMiddleware,
      searchSessionMiddleware,
      searchWidgetDictationMiddleware,
      voiceChatModeListener.middleware,
      onRunCommandListener.middleware,
      tickListener.middleware,
      onThreadIdListener.middleware,
    ]);
  },
});

const THROTTLE_TIME_IN_MS = 200;
const throttledSaveToLocalStorage = throttle(() => {
  const state = store.getState();
  const live = state.live;
  window.localStorage.setItem('live', JSON.stringify(live));

  const token = window.localStorage.getItem('token');
  if (!token) return;
  const uuid = getDecodedToken(token)?.uuid;

  if (
    uuid &&
    token &&
    state.appSettings.userSpecificSettings &&
    state.appSettings.userSpecificSettings[uuid]
  ) {
    const serializedUserSettings = JSON.parse(
      window.localStorage.getItem('userSpecificSettings') ?? '{}',
    ) as UserSpecificSettingsType;
    const userSettings = state.appSettings.userSpecificSettings[uuid];

    const settings: UserSpecificSettingsType = {
      ...serializedUserSettings,
      ...state.appSettings.userSpecificSettings,
      [uuid]: {
        shouldSaveUploadsToKnowledgeBase:
          userSettings.shouldSaveUploadsToKnowledgeBase,
        searchScope: userSettings.searchScope,
        customStyle: userSettings.customStyle,
        useCustomStyle: userSettings.useCustomStyle,
        responseLength: userSettings.responseLength,
        searchMode: userSettings.searchMode,
        researchAdvancedTime: userSettings.researchAdvancedTime,
        bot: userSettings.bot,
        voice: userSettings.voice,
        advancedPdfIngestion: userSettings.advancedPdfIngestion,
      },
    };
    window.localStorage.setItem(
      'userSpecificSettings',
      JSON.stringify(settings),
    );
  }
}, THROTTLE_TIME_IN_MS);

store.subscribe(throttledSaveToLocalStorage);

export type RootType = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
